/**
 * @owner @Appboy/operator
 * @fileoverview Retrieves the singleton instance of the pusher,
 * created in React via `dashboard-require-init.ko.js.coffee`
 */

import { featureOn } from "@lib/featureFlipper";
import { BrazePusher } from "@lib/pusher/BrazePusher";

import type { PusherConnectionDiagnostics, PusherSingletonOptions } from "./types";

// Create a single instance that will be reused
const reactPusherInstance: { current: BrazePusher | null } = { current: null };

/**
 * @param {Object} [options]
 * @param {boolean} [options.useReactPusher] whether to return the new React-based pusher or the old one
 * initialized via `dashboard-require-init.ko.js.coffee`
 * @returns {Object} the pusher instance
 */
export const getPusherSingleton = ({
  useReactPusher = true,
  railsEnv,
  pusherKey,
  pusherCluster,
  pusherEnabled = true,
}: PusherSingletonOptions = {}): BrazePusher => {
  if (!useReactPusher) {
    return (window as any).appboy.pusher;
  }

  const isDevOrStaging = railsEnv === "development" || railsEnv === "staging";
  const pcePusherEnabled = featureOn("pce_pusher_enabled");

  // If using the new React-based pusher, create the singleton instance if it doesn't exist
  if (!reactPusherInstance.current) {
    if (pusherKey === undefined || pusherCluster === undefined) {
      throw new Error("pusherKey and pusherCluster are required to create the React Pusher singleton");
    }
    const overridePusherEnabled = isDevOrStaging ? pusherEnabled || pcePusherEnabled : pusherEnabled;
    reactPusherInstance.current = new BrazePusher(
      pusherKey,
      pusherCluster,
      isDevOrStaging,
      overridePusherEnabled ?? true
    );
  }

  return reactPusherInstance.current;
};

/**
 * Returns Pusher connection diagnostics from the React-based singleton if it
 * exists. Safe to call even when no BrazePusher instance has been created —
 * returns null. Intended for error logging to disambiguate Pusher-related
 * failures. Mirrors `getDustConnectionDiagnostics` from `@lib/dust`.
 */
export const getPusherConnectionDiagnostics = (): PusherConnectionDiagnostics | null => {
  return reactPusherInstance.current?.getConnectionDiagnostics() ?? null;
};
